import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  LinkOutlined,
  ReloadOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import axiosAuth from "../utils/axiosAuth";
import "./styles/StormOps.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.fans.nextremedy.net";

function loadDraft() {
  try {
    const raw = sessionStorage.getItem("stormops.previewDraft");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function formatDate(value) {
  if (!value) return "Not recorded";
  if (value._seconds) return new Date(value._seconds * 1000).toLocaleString();
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Not recorded" : date.toLocaleString();
}

function runStatus(run) {
  if (run.status === "completed") return "completed";
  if (run.stormOps?.productionStatus === "queued") return "queued";
  if (run.stormOps?.productionStatus === "running") return "running";
  if (run.paymentStatus === "paid") return "paid";
  return "awaiting payment";
}

function publicUrl(slug) {
  if (!slug) return "";
  return `${window.location.origin}/public/stormops/${slug}`;
}

export default function StormOpsWorkspace() {
  const navigate = useNavigate();
  const draft = useMemo(loadDraft, []);
  const [zip, setZip] = useState(draft?.zip || "");
  const [serviceType, setServiceType] = useState(draft?.serviceType || "");
  const [businessName, setBusinessName] = useState("");
  const [publicPhone, setPublicPhone] = useState("");
  const [preview, setPreview] = useState(draft || null);
  const [runs, setRuns] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [error, setError] = useState("");

  const fetchRuns = async () => {
    setLoadingRuns(true);
    try {
      const response = await axiosAuth.get("/api/stormops/runs");
      setRuns(response.data?.data?.runs || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Unable to load history");
    } finally {
      setLoadingRuns(false);
    }
  };

  useEffect(() => {
    fetchRuns();
    const interval = setInterval(fetchRuns, 15000);
    return () => clearInterval(interval);
  }, []);

  const runPreview = async (event) => {
    event?.preventDefault();
    setError("");
    setLoadingPreview(true);

    try {
      const response = await fetch(`${API_BASE}/api/stormops/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zip, serviceType }),
      });
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Preview failed");
      }

      const nextPreview = {
        zip,
        serviceType,
        preview: payload.data.preview,
        sourceArtifacts: payload.data.sourceArtifacts,
        providerArtifacts: payload.data.providerArtifacts,
        selectedDirection: payload.data.preview?.recommendedDirection || "",
      };
      sessionStorage.setItem("stormops.previewDraft", JSON.stringify(nextPreview));
      setPreview(nextPreview);
      return nextPreview;
    } catch (err) {
      setError(err.message || "Unable to create preview");
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  const createRun = async () => {
    setError("");
    setCreating(true);

    try {
      let currentPreview = preview;
      if (!currentPreview || currentPreview.zip !== zip || currentPreview.serviceType !== serviceType) {
        currentPreview = await runPreview();
      }
      if (!currentPreview) return;

      const response = await axiosAuth.post("/api/stormops/runs", {
        zip,
        serviceType,
        businessName,
        publicPhone,
        selectedDirection: currentPreview.selectedDirection,
        preview: {
          preview: currentPreview.preview,
          sourceArtifacts: currentPreview.sourceArtifacts,
          providerArtifacts: currentPreview.providerArtifacts,
        },
      });

      const campaignId = response.data?.data?.campaignId;
      if (!campaignId) throw new Error("Run was created without a campaign ID");

      sessionStorage.removeItem("stormops.previewDraft");
      await fetchRuns();
      navigate(`/campaigns/${campaignId}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Unable to create paid run");
    } finally {
      setCreating(false);
    }
  };

  const copyPublicLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(publicUrl(slug));
    } catch (error) {
      setError("Public link is ready, but clipboard access was blocked.");
    }
  };

  return (
    <main className="storm-shell storm-shell-workspace">
      <header className="storm-nav">
        <Link className="storm-brand" to="/">
          <span className="storm-brand-mark">S</span>
          <span>StormOps Dispatch</span>
        </Link>
        <button className="storm-icon-button" type="button" onClick={fetchRuns} disabled={loadingRuns} aria-label="Refresh runs">
          <ReloadOutlined />
        </button>
      </header>

      <section className="storm-workspace-grid">
        <div className="storm-production-panel">
          <p className="storm-kicker">Response run</p>
          <h1>Prepare the paid storm response kit.</h1>

          <form className="storm-form storm-form-stack" onSubmit={runPreview}>
            <label>
              ZIP code
              <input value={zip} onChange={(event) => setZip(event.target.value)} inputMode="numeric" maxLength="5" required />
            </label>
            <label>
              Service type
              <input value={serviceType} onChange={(event) => setServiceType(event.target.value)} required />
            </label>
            <label>
              Business name
              <input value={businessName} onChange={(event) => setBusinessName(event.target.value)} placeholder="Service company name" />
            </label>
            <label>
              Public phone
              <input value={publicPhone} onChange={(event) => setPublicPhone(event.target.value)} placeholder="Optional" />
            </label>
            <div className="storm-button-row">
              <button className="storm-secondary" type="submit" disabled={loadingPreview}>
                <ThunderboltOutlined />
                {loadingPreview ? "Refreshing" : "Refresh Preview"}
              </button>
              <button className="storm-primary" type="button" onClick={createRun} disabled={creating || loadingPreview}>
                <CreditCardOutlined />
                {creating ? "Creating" : "Create GBP 49 Run"}
              </button>
            </div>
          </form>

          {error && <div className="storm-alert storm-alert-error">{error}</div>}

          {preview && (
            <div className="storm-inline-preview">
              <span className={`storm-status storm-status-${preview.preview?.risk?.riskLevel || "standby"}`}>
                {preview.preview?.risk?.label || "Preview ready"}
              </span>
              <h2>{preview.preview?.recommendedDirection}</h2>
              <p>{preview.preview?.sourceLabel}</p>
              <div className="storm-source-line">Normalized source hash: {preview.sourceArtifacts?.normalizedHash}</div>
            </div>
          )}
        </div>

        <div className="storm-history-panel">
          <div className="storm-section-heading">
            <div>
              <p className="storm-kicker">History</p>
              <h2>Response runs</h2>
            </div>
            <HistoryOutlined />
          </div>

          {runs.length === 0 ? (
            <div className="storm-empty-history">
              <SafetyOutlined />
              <p>No StormOps runs yet.</p>
            </div>
          ) : (
            <div className="storm-run-list">
              {runs.map((run) => {
                const slug = run.stormOps?.publicSlug;
                const page = run.stormOps?.publicPage;
                const status = runStatus(run);

                return (
                  <article className="storm-run-card" key={run.id}>
                    <div className="storm-run-topline">
                      <span className={`storm-status storm-status-${status.replace(/\s+/g, "-")}`}>{status}</span>
                      <span>{formatDate(run.createdAt)}</span>
                    </div>
                    <h3>{run.stormOps?.serviceType || run.serviceType} in {run.stormOps?.zip || run.searchTerm}</h3>
                    <p>{run.progress || "Waiting for payment"}</p>

                    <div className="storm-run-facts">
                      <span>Payment: {run.paymentStatus || "unpaid"}</span>
                      <span>Progress: {run.percentComplete || 0}%</span>
                      <span>Source: {run.stormOps?.sourceArtifacts?.normalizedHash?.slice(0, 12) || "pending"}</span>
                    </div>

                    {page && (
                      <div className="storm-output-strip">
                        <CheckCircleOutlined />
                        <div>
                          <strong>{page.title}</strong>
                          <span>{page.location}</span>
                        </div>
                      </div>
                    )}

                    <div className="storm-button-row storm-run-actions">
                      <Link className="storm-secondary storm-link-button" to={`/campaigns/${run.id}`}>
                        <HistoryOutlined />
                        Campaign Detail
                      </Link>
                      {run.paymentStatus !== "paid" && (
                        <Link className="storm-primary storm-link-button" to={`/payment/${run.id}`}>
                          <CreditCardOutlined />
                          Activate Run
                        </Link>
                      )}
                      {page && slug && (
                        <>
                          <Link className="storm-secondary storm-link-button" to={`/public/stormops/${slug}`}>
                            <LinkOutlined />
                            Public Page
                          </Link>
                          <button className="storm-icon-button" type="button" onClick={() => copyPublicLink(slug)} aria-label="Copy public link">
                            <LinkOutlined />
                          </button>
                        </>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
